/**
 * CORE-BK responsibility 9 — internal privacy/security control posture.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * THIS PAGE IS AN OBSERVER
 * ═══════════════════════════════════════════════════════════════════════════
 * It renders what `privacy_posture()` derives from live evidence. There is no
 * "Enable retention", no "Rotate now", no "Fix RLS" — a reporter that can
 * repair the thing it reports on will eventually report on itself.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * IT IS INTERNAL, AND IT IS NOT A COMPLIANCE CLAIM
 * ═══════════════════════════════════════════════════════════════════════════
 * Internal platform administrators only — not support operators, not shop
 * owners or managers, not customers. The three states describe whether OptiBay
 * CONTROLS ARE OPERATING; they are deliberately not COMPLIANT/CERTIFIED, and
 * there is no score, grade or percentage. Averaging controls with different
 * constitutional meaning would manufacture exactly the false reassurance
 * responsibility 9 exists to prevent.
 *
 * A board where every card is green would be the bug, not the goal.
 */
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  AlertTriangle, CheckCircle2, HelpCircle, Loader2, RefreshCw, ShieldCheck,
} from 'lucide-react';
import { TopBar } from '@/components/layout/TopBar';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Card, CardContent, CardDescription, CardHeader, CardTitle,
} from '@/components/ui/card';
import { supabase } from '@/integrations/supabase/client';
// Core-100 R4 W4 (CORE-BK): the retention registry's read-only admin surface.
import { RetentionRegistryList } from '@/components/admin/RetentionRegistryList';

type PostureStatus = 'ENFORCED' | 'AVAILABLE_NOT_VERIFIED' | 'NOT_OPERATING';

interface PostureControl {
  control_id: string;
  name: string;
  scope: string;
  status: PostureStatus;
  summary: string;
  evidence_type: string;
  evidence_observed_at: string;
  evidence_details: Record<string, unknown>;
  limitations: string[];
}

interface PosturePayload {
  generated_at: string;
  schema_version: string;
  counts: Record<string, number>;
  notice: string;
  controls: PostureControl[];
}

/**
 * Status presentation never relies on colour alone: each state carries its own
 * icon and its own words, so the distinction survives a screen reader and a
 * monochrome display.
 */
const STATUS_PRESENTATION: Record<PostureStatus, {
  label: string; hint: string; icon: typeof CheckCircle2; className: string;
}> = {
  ENFORCED: {
    label: 'Enforced',
    hint: 'Operating for its stated scope, with current evidence.',
    icon: CheckCircle2,
    className: 'border-emerald-600 text-emerald-700 dark:text-emerald-400',
  },
  AVAILABLE_NOT_VERIFIED: {
    label: 'Available — not verified',
    hint: 'The mechanism exists, but operation for this scope is unproven or incomplete.',
    icon: HelpCircle,
    className: 'border-amber-600 text-amber-700 dark:text-amber-400',
  },
  NOT_OPERATING: {
    label: 'Not operating',
    hint: 'Expected to operate; evidence shows it is not.',
    icon: AlertTriangle,
    className: 'border-red-600 text-red-700 dark:text-red-400',
  },
};

const EVIDENCE_LABEL: Record<string, string> = {
  RUNTIME_DATABASE_CATALOGUE: 'Live database catalogue',
  RUNTIME_DATABASE_EVIDENCE: 'Live database evidence',
  REPOSITORY_DECLARED: 'Declared in source — no runtime evidence',
  EVIDENCE_UNAVAILABLE: 'Evidence could not be read',
};

export default function PrivacyPosture() {
  const navigate = useNavigate();
  const [posture, setPosture] = useState<PosturePayload | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = async () => {
    setIsLoading(true);
    setError(null);
    const { data, error: rpcError } = await (supabase as unknown as {
      rpc: (f: string) => Promise<{ data: unknown; error: { message?: string } | null }>;
    }).rpc('privacy_posture');

    if (rpcError || !data) {
      // Failing to read posture must not render a remembered green board.
      setPosture(null);
      setError('Could not read the current control posture.');
    } else {
      setPosture(data as PosturePayload);
    }
    setIsLoading(false);
  };

  useEffect(() => { void load(); }, []);

  return (
    <div className="flex flex-col h-screen bg-background">
      <TopBar />
      <main className="flex-1 overflow-auto">
        <div className="max-w-4xl mx-auto p-6 space-y-6">
          <div className="flex items-start justify-between gap-4">
            <div>
              <h1 className="text-3xl font-bold flex items-center gap-2">
                <ShieldCheck className="h-7 w-7" />
                Privacy &amp; Security Posture
              </h1>
              <p className="text-muted-foreground mt-1">
                Whether each OptiBay control is actually operating in this environment.
              </p>
            </div>
            <div className="flex gap-2 shrink-0">
              <Button variant="outline" onClick={() => void load()} disabled={isLoading}>
                <RefreshCw className={`mr-2 h-4 w-4 ${isLoading ? 'animate-spin' : ''}`} />
                Refresh
              </Button>
              <Button variant="outline" onClick={() => navigate(-1)}>Back</Button>
            </div>
          </div>

          {/* Stated up front rather than in a footnote. */}
          <Card>
            <CardContent className="p-4 text-sm text-muted-foreground space-y-2">
              <p>
                These states describe whether OptiBay <strong>controls are operating</strong>.
                They are not a statement of compliance with any data-protection law, and there
                is deliberately no overall score.
              </p>
              <ul className="space-y-1">
                {(Object.keys(STATUS_PRESENTATION) as PostureStatus[]).map(s => {
                  const p = STATUS_PRESENTATION[s];
                  const Icon = p.icon;
                  return (
                    <li key={s} className="flex items-start gap-2">
                      <Icon className={`h-4 w-4 mt-0.5 shrink-0 ${p.className}`} aria-hidden="true" />
                      <span><strong>{p.label}</strong> — {p.hint}</span>
                    </li>
                  );
                })}
              </ul>
            </CardContent>
          </Card>

          {isLoading && (
            <div className="flex items-center justify-center py-16" role="status">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
              <span className="sr-only">Reading control posture…</span>
            </div>
          )}

          {error && !isLoading && (
            <Card className="border-red-600">
              <CardContent className="p-4 flex items-start gap-2">
                <AlertTriangle className="h-5 w-5 text-red-600 mt-0.5 shrink-0" aria-hidden="true" />
                <div className="text-sm">
                  <p className="font-medium">{error}</p>
                  <p className="text-muted-foreground">
                    No posture is shown rather than a previously remembered one.
                  </p>
                </div>
              </CardContent>
            </Card>
          )}

          {posture && !isLoading && (
            <>
              <p className="text-sm text-muted-foreground" data-testid="posture-counts">
                {posture.counts.ENFORCED ?? 0} enforced ·{' '}
                {posture.counts.AVAILABLE_NOT_VERIFIED ?? 0} available, not verified ·{' '}
                {posture.counts.NOT_OPERATING ?? 0} not operating
                {' — '}observed {new Date(posture.generated_at).toLocaleString()}
              </p>

              <div className="space-y-4">
                {posture.controls.map(c => {
                  const p = STATUS_PRESENTATION[c.status] ?? STATUS_PRESENTATION.AVAILABLE_NOT_VERIFIED;
                  const Icon = p.icon;
                  return (
                    <Card key={c.control_id} className={`border-l-4 ${p.className}`}>
                      <CardHeader className="pb-3">
                        <div className="flex items-start justify-between gap-3">
                          <div className="min-w-0">
                            <CardTitle className="text-base flex items-center gap-2">
                              <Icon className={`h-5 w-5 shrink-0 ${p.className}`} aria-hidden="true" />
                              {c.name}
                            </CardTitle>
                            {/* Scope is part of the answer, not a footnote: a
                                bounded green must never read as a broad one. */}
                            <CardDescription className="mt-1">
                              <span className="font-medium">Scope:</span> {c.scope}
                            </CardDescription>
                          </div>
                          <Badge variant="outline" className={`shrink-0 ${p.className}`}>
                            {p.label}
                          </Badge>
                        </div>
                      </CardHeader>
                      <CardContent className="space-y-3 text-sm">
                        <p>{c.summary}</p>

                        {c.limitations?.length > 0 && (
                          <div>
                            <p className="font-medium text-muted-foreground">Limitations</p>
                            <ul className="list-disc pl-5 text-muted-foreground space-y-1">
                              {c.limitations.map((l, i) => <li key={i}>{l}</li>)}
                            </ul>
                          </div>
                        )}

                        <p className="text-xs text-muted-foreground">
                          Evidence: {EVIDENCE_LABEL[c.evidence_type] ?? c.evidence_type}
                          {' · observed '}
                          {new Date(c.evidence_observed_at).toLocaleString()}
                        </p>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>

              <RetentionRegistryList />
            </>
          )}
        </div>
      </main>
    </div>
  );
}
